import { db } from '../../lib/firebase';
import { collection, query, where, doc, getDoc, setDoc, updateDoc, getDocs, serverTimestamp, arrayUnion, Timestamp, deleteDoc, writeBatch, onSnapshot, runTransaction } from 'firebase/firestore';
import type { MonthlyData } from '../../types/finance';
import { ServiceError, validationError, permissionError } from '../../utils/ServiceError';
import { mapToMonthlyData } from './helpers';

export const financeSummary = {
    /**
     * Obtener resumen financiero mensual
     */
    getFinancialData: async (
        franchiseId: string,
        month: string
    ): Promise<MonthlyData | null> => {
        try {
            if (!franchiseId || !month) return null;

            const docId = `${franchiseId}_${month}`;
            const docRef = doc(db, 'financial_summaries', docId);
            const docSnap = await getDoc(docRef);

            if (docSnap.exists()) {
                return mapToMonthlyData(docSnap, franchiseId);
            }

            return null;
        } catch (error) {
            throw new ServiceError('getFinancialData', { cause: error });
        }
    },

    /**
     * Subscribe to monthly financial data (Real-time)
     */
    subscribeToFinancialData: (
        franchiseId: string,
        month: string,
        onUpdate: (data: MonthlyData | null) => void,
        onError: (error: Error) => void
    ): (() => void) => {
        if (!franchiseId || !month) return () => { };

        const docId = `${franchiseId}_${month}`;
        const docRef = doc(db, 'financial_summaries', docId);

        return onSnapshot(
            docRef,
            (docSnap) => {
                if (docSnap.exists()) {
                    onUpdate(mapToMonthlyData(docSnap, franchiseId));
                } else {
                    onUpdate(null);
                }
            },
            (error) => {
                console.error("Error subscribing to financial data:", error);
                onError(new ServiceError('subscribeToFinancialData', { cause: error }));
            }
        );
    },

    /**
     * Guardar/Actualizar resumen mensual
     */
    updateFinancialData: async (
        franchiseId: string,
        month: string,
        data: Partial<MonthlyData>
    ): Promise<void> => {
        try {
            if (!franchiseId) {
                throw permissionError('updateFinancialData', 'Franchise ID is required');
            }

            if (!month) {
                throw validationError('updateFinancialData', 'Month is required');
            }

            if (!month.match(/^\d{4}-\d{2}$/)) {
                throw validationError('updateFinancialData', `Invalid format. Expected YYYY-MM, received ${month}`);
            }

            const docId = `${franchiseId}_${month}`;
            const docRef = doc(db, 'financial_summaries', docId);

            // Transacción: el merge depende del estado existente (read-then-write);
            // sin ella dos guardados concurrentes se pisan silenciosamente.
            await runTransaction(db, async (transaction) => {
                const existingSnap = await transaction.get(docRef);
                const existing: Record<string, unknown> = existingSnap.exists() ? existingSnap.data() : {};

                const existingRevenue = Number(existing.totalIncome ?? existing.revenue ?? 0);
                const existingExpenses = Number(existing.totalExpenses ?? existing.expenses ?? 0);
                const existingStatus = existing.status;
                const existingIsLocked = existing.isLocked ?? existing.is_locked ?? false;

                const hasIncome = data.totalIncome !== undefined || data.revenue !== undefined;
                const hasExpenses = data.totalExpenses !== undefined || data.expenses !== undefined;

                const nextRevenue = Number(hasIncome ? (data.totalIncome ?? data.revenue ?? 0) : existingRevenue);
                const nextExpenses = Number(hasExpenses ? (data.totalExpenses ?? data.expenses ?? 0) : existingExpenses);
                const nextProfit = nextRevenue - nextExpenses;

                // Un documento nuevo nace ABIERTO. Con el default `approved` una
                // simple escritura de datos daba el mes por revisado y lo bloqueaba.
                // `?? 'open'` garantiza valor, pero `MonthlyData['status']` es opcional y el
                // tipo seguia admitiendo undefined. Se excluye explicitamente.
                const nextStatus = (data.status ?? existingStatus ?? 'open') as NonNullable<MonthlyData['status']>;

                let nextIsLocked = (data.isLocked ?? (data as Record<string, unknown>).is_locked ?? existingIsLocked) as boolean;

                if (nextStatus === 'open') nextIsLocked = false;
                else if (['submitted', 'locked', 'unlock_requested', 'approved'].includes(nextStatus)) {
                    nextIsLocked = true;
                }

                const sanitizedData: Partial<MonthlyData> & Record<string, unknown> = {
                    ...data,
                    totalIncome: nextRevenue,
                    totalExpenses: nextExpenses,
                    grossIncome: nextRevenue,
                    revenue: nextRevenue,
                    expenses: nextExpenses,
                    profit: nextProfit,
                    isLocked: nextIsLocked,
                    is_locked: nextIsLocked
                };

                if (data.breakdown !== undefined) {
                    sanitizedData.breakdown = Object.fromEntries(
                        Object.entries(data.breakdown || {}).map(([k, v]) => [k, Number(v) || 0])
                    );
                }

                transaction.set(docRef, {
                    ...sanitizedData,
                    franchiseId,
                    franchise_id: franchiseId,
                    month,
                    status: nextStatus,
                    updatedAt: serverTimestamp(),
                    updated_at: serverTimestamp()
                }, { merge: true });
            });
        } catch (error) {
            if (error instanceof ServiceError) throw error;
            throw new ServiceError('updateFinancialData', { cause: error });
        }
    },

    /**
     * Obtener historial de cierres
     */
    fetchClosures: async (
        franchiseId: string
    ): Promise<MonthlyData[]> => {
        try {
            if (!franchiseId) {
                throw permissionError('fetchClosures', 'Franchise ID is required');
            }

            const q = query(
                collection(db, 'financial_summaries'),
                where('franchiseId', '==', franchiseId)
            );

            const snapshot = await getDocs(q);

            return snapshot.docs
                .map(docSnap => mapToMonthlyData(docSnap, franchiseId))
                .filter(item => item.status !== 'deleted');
        } catch (error) {
            if (error instanceof ServiceError) throw error;
            throw new ServiceError('fetchClosures', { cause: error });
        }
    },

    /**
     * Get financial data for a specific year
     */
    getFinancialYearlyData: async (
        franchiseId: string,
        year: number
    ): Promise<MonthlyData[]> => {
        if (!franchiseId) return [];

        try {
            const data = await financeSummary.fetchClosures(franchiseId);

            return data.filter((record: MonthlyData) => {
                if (record.month) {
                    return record.month.startsWith(`${year}-`);
                }
                if (record.date) {
                    const dateVal = record.date as unknown;
                    const d = dateVal instanceof Date ? dateVal : (dateVal as { toDate: () => Date }).toDate();
                    return d.getFullYear() === year;
                }
                return false;
            });
        } catch (error) {
            console.error('[FinanceSummary] Error fetching yearly data:', error);
            throw new ServiceError('getFinancialYearlyData', { cause: error });
        }
    },

    /**
     * UNLOCK MONTH: Allow franchise to edit closed month
     */
    unlockMonth: async (
        franchiseId: string,
        month: string
    ): Promise<void> => {
        try {
            if (!franchiseId || !month) {
                throw validationError('unlockMonth', 'Missing args');
            }

            const docId = `${franchiseId}_${month}`;
            const docRef = doc(db, 'financial_summaries', docId);

            await updateDoc(docRef, {
                status: 'open',
                isLocked: false,
                is_locked: false,
                updatedAt: serverTimestamp(),
                updated_at: serverTimestamp(),
                statusHistory: arrayUnion({
                    status: 'open',
                    timestamp: Timestamp.now(),
                    action: 'unlocked_by_admin'
                })
            });
        } catch (error) {
            if (error instanceof ServiceError) throw error;
            throw new ServiceError('unlockMonth', { cause: error });
        }
    },

    /**
     * REQUEST UNLOCK: Franchise requests to edit a locked month
     */
    requestUnlock: async (
        franchiseId: string,
        month: string,
        reason: string
    ): Promise<void> => {
        try {
            const docId = `${franchiseId}_${month}`;
            const docRef = doc(db, 'financial_summaries', docId);

            await updateDoc(docRef, {
                status: 'unlock_requested',
                unlockReason: reason,
                updatedAt: serverTimestamp(),
                statusHistory: arrayUnion({
                    status: 'unlock_requested',
                    timestamp: Timestamp.now(),
                    reason: reason,
                    action: 'requested_by_franchise'
                })
            });
        } catch (error) {
            throw new ServiceError('requestUnlock', { cause: error });
        }
    },

    /**
     * DELETE SUMMARY BY ID
     */
    deleteSummaryDocument: async (docId: string): Promise<void> => {
        try {
            const docRef = doc(db, 'financial_summaries', docId);
            await deleteDoc(docRef);
        } catch (error) {
            throw new ServiceError('deleteSummaryDocument', { cause: error });
        }
    },

    /**
     * Delete the visible monthly closure without touching locked fiscal source records.
     *
     * Closed months may have immutable financial_records/tax_vault entries. The history
     * table stores its rows in financial_summaries, so removing the closure should only
     * delete that summary document and leave source records intact.
     */
    deleteMonthlyClosure: async (
        franchiseId: string,
        monthKey: string
    ): Promise<void> => {
        try {
            if (!franchiseId) {
                throw permissionError('deleteMonthlyClosure', 'Franchise ID is required');
            }

            if (!monthKey) {
                throw validationError('deleteMonthlyClosure', 'Month is required');
            }

            if (!monthKey.match(/^\d{4}-\d{2}$/)) {
                throw validationError('deleteMonthlyClosure', `Invalid format. Expected YYYY-MM, received ${monthKey}`);
            }

            const docId = `${franchiseId}_${monthKey}`;
            const summaryRef = doc(db, 'financial_summaries', docId);
            await deleteDoc(summaryRef);
        } catch (error) {
            if (error instanceof ServiceError) throw error;
            throw new ServiceError('deleteMonthlyClosure', { cause: error });
        }
    },

    /**
     * Reset month summary (delete all records and summary)
     */
    resetMonthSummary: async (
        franchiseId: string,
        monthKey: string
    ): Promise<void> => {
        const batch = writeBatch(db);

        const [year, month] = monthKey.split('-').map(Number);
        const startDate = new Date(year, month - 1, 1);
        // Cota superior EXCLUSIVA (primer día del mes siguiente): con `<= último día
        // a las 00:00` se perdían los registros del último día con hora.
        const endExclusive = new Date(year, month, 1);

        const recordsQuery = query(
            collection(db, 'financial_records'),
            where('franchise_id', '==', franchiseId),
            where('date', '>=', startDate),
            where('date', '<', endExclusive)
        );

        const recordsSnap = await getDocs(recordsQuery);
        recordsSnap.docs.forEach(doc => {
            batch.delete(doc.ref);
        });

        const docId = `${franchiseId}_${monthKey}`;
        const summaryRef = doc(db, 'financial_summaries', docId);
        batch.delete(summaryRef);

        await batch.commit();
    },

    /**
     * FORCE SYNC: Recalculates summary for a specific month
     */
    recalculateMonthSummary: async (
        franchiseId: string,
        monthKey: string
    ): Promise<void> => {
        try {
            const [year, month] = monthKey.split('-').map(Number);
            const startDate = new Date(year, month - 1, 1);
            // Cota superior exclusiva — ver resetMonthSummary
            const endExclusive = new Date(year, month, 1);

            const q = query(
                collection(db, 'financial_records'),
                where('franchise_id', '==', franchiseId),
                where('date', '>=', startDate),
                where('date', '<', endExclusive)
            );

            const snap = await getDocs(q);

            let totalIncome = 0;
            let totalExpenses = 0;
            let accLogisticsOnly = 0;

            const breakdown: Record<string, number> = {};

            snap.docs.forEach(doc => {
                const d = doc.data();
                if (d.status === 'draft' || d.status === 'rejected') return;

                const rev = Number(d.revenue || (d.type === 'income' ? d.amount : 0) || 0);
                const exp = Number(d.expenses || (d.type === 'expense' ? d.amount : 0) || 0);

                totalIncome += rev;
                totalExpenses += exp;
                if (d.logisticsIncome) accLogisticsOnly += Number(d.logisticsIncome);

                if (d.breakdown) {
                    Object.keys(d.breakdown).forEach(k => {
                        breakdown[k] = (breakdown[k] || 0) + (Number(d.breakdown[k]) || 0);
                    });
                }
            });

            const summaryId = `${franchiseId}_${monthKey}`;
            const summaryRef = doc(db, 'financial_summaries', summaryId);

            // Recalcular es una operación de datos, no una aprobación: el estado del
            // mes lo decide quien lo cierra, así que no se toca aquí.
            await setDoc(summaryRef, {
                franchiseId,
                franchise_id: franchiseId,
                month: monthKey,
                totalIncome,
                totalExpenses,
                grossIncome: totalIncome,
                revenue: totalIncome,
                expenses: totalExpenses,
                // `profit` canónico = pre-impuestos. No se suman los `d.profit` de los
                // registros porque los legacy mezclaban pre y post-IRPF.
                profit: totalIncome - totalExpenses,
                logisticsIncome: accLogisticsOnly,
                breakdown,
                updatedAt: serverTimestamp(),
                updated_at: serverTimestamp(),
                lastForceSync: serverTimestamp()
            }, { merge: true });
        } catch (error) {
            throw new ServiceError('recalculateMonthSummary', { cause: error });
        }
    }
};
